import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Button, Row, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions, adminActions } from '../../redux/_actions';
import TeamForm from './teamform';
import TeamDetail from './teamdetail';
import TableComponent from '../helpers/tablecomponent';
import { modeType } from '../../utilities/constants';
import { Loading, ScrollLoading } from '../helpers/loading';
import { SearchInput, addBold, noResults } from '../helpers/search';

function TeamAdmin(props) {
  const { setIsForm } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [isScrollLoading, setIsScrollLoading] = useState(false);
  const { LIST, DETAIL, EDIT, DELETE, ADD } = modeType;
  const [mode, setMode] = useState(LIST);
  const [teamId, setTeamId] = useState(null);
  const alert = useSelector((state) => state.alert);
  const teams = useSelector((state) => state.admin.teams);
  const rosters = useSelector((state) => state.admin.rosters);
  const rosterLinks = useSelector((state) => state.admin.rosterLinks);
  const leagues = useSelector((state) => state.admin.leagues);
  const nextToken = useSelector((state) => state.admin.teams.tokens.nextToken);
  const searchQuery = useSelector(
    (state) => state.admin.teams.tokens.searchQuery
  );
  const searchToken = useSelector(
    (state) => state.admin.teams.tokens.searchToken
  );

  const [searchTerm, setSearchTerm] = useState(searchQuery || '');
  const [tempSearchTerm, setTempSearchTerm] = useState(searchQuery || ''); // used for async holding of query
  const dispatch = useDispatch();
  const numTeams = teams?.allIds?.length || 0;
  const isSearch = searchTerm !== '';
  let lastScrollTop = 0;

  useEffect(() => {
    if (!alert.clear) {
      setIsLoading(false);
    }
  }, [alert.clear]);

  useEffect(() => {
    dispatch(alertActions.clear());
  }, [dispatch]);

  useEffect(() => {
    if (numTeams === 0) {
      setIsLoading(true);
      dispatch(adminActions.getTeams());
    } else {
      setIsLoading(false);
    }
  }, [numTeams, dispatch]);

  useEffect(() => {
    setIsScrollLoading(false);
    setSearchTerm(tempSearchTerm);
  }, [teams, tempSearchTerm]);

  const teamDetail = (e, row) => {
    if (!alert.clear) {
      dispatch(alertActions.clear());
    }
    setTeamId(row.id);
    setMode(DETAIL);
    setIsForm(true);
  };

  const back = () => {
    setMode(LIST);
    setIsForm(false);
  };

  const add = () => {
    if (!alert.clear) {
      dispatch(alertActions.clear());
    }
    setMode(ADD);
    setIsForm(true);
  };

  const headers = useMemo(
    () => [
      { Header: '#', accessor: 'order' },
      {
        Header: 'Name',
        accessor: 'name',
        Cell: (r) => addBold(r.cell.value, searchTerm),
      },
      { Header: 'Type', accessor: 'type' },
      { Header: 'Gender', accessor: 'gender' },
      { Header: 'Athletes', accessor: 'athletes' },
      { Header: 'Roster', accessor: 'roster' },
      { Header: 'League', accessor: 'league' },
    ],
    [searchTerm]
  );

  const data = useMemo(() => {
    const checkRosterSize = (roster) => {
      return roster.athletes.items.filter((id) => rosterLinks.byId[id].active)
        .length;
    };

    return teams.allIds.map((id, i) => {
      const { name, type, gender } = teams.byId[id];
      const currentRoster = teams.byId[id]?.rosters?.items
        ? rosters.byId[
            teams.byId[id].rosters?.items.slice().sort((a, b) => {
              return a.activeDate < b.activeDate;
            })?.[0]
          ]
        : null;
      const currentLeague = teams.byId[id]?.leagues?.items
        ? leagues.byId[teams.byId[id].leagues.items[0]]
        : null;

      return {
        order: i + 1,
        name: name,
        type: type,
        gender: gender,
        athletes: currentRoster ? checkRosterSize(currentRoster) : 'N/A',
        roster: currentRoster?.title || '',
        league: currentLeague ? currentLeague.name : 'N/A',
        id: id,
      };
    });
  }, [teams, rosterLinks.byId, leagues.byId, rosters.byId]);

  const table = () => {
    const initialState = {
      sortBy: [{ id: 'name', desc: false }],
    };

    const empty = numTeams === 0 || data.length === 0;

    return empty ? (
      noResults('teams')
    ) : (
      <TableComponent
        columns={headers}
        data={data}
        onClick={teamDetail}
        initialState={initialState}
        textSort={true}
        searchTerm={searchTerm}
        showFooter={true}
        filterColumns={['name']}
      />
    );
  };

  const handleScroll = (e) => {
    const { scrollHeight, scrollTop, scrollLeft, clientHeight } = e.target;
    const bottom = scrollHeight - scrollTop === clientHeight;
    const nextPage =
      (isSearch && searchToken !== null) ||
      (!isSearch && nextToken !== null && numTeams !== 0);
    const vertScroll =
      scrollLeft === 0 && scrollTop !== lastScrollTop && lastScrollTop !== 0;

    lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

    if (bottom && nextPage && vertScroll && !isScrollLoading) {
      setIsScrollLoading(true);
      dispatch(
        adminActions.getTeams({
          searchQuery: searchTerm,
          nextToken: isSearch ? searchToken : nextToken,
        })
      );
    }
  };

  const handleSearch = (query) => {
    setTempSearchTerm(query);

    if (query && query !== '') {
      dispatch(adminActions.getTeams({ searchQuery: query, nextToken: null }));
    } else {
      // clear search
      setSearchTerm(query);
    }
  };

  const adminBody = () => {
    return (
      <>
        {!alert.clear ? (
          <Row style={{ padding: '0 15px', margin: '1rem -15px' }}>
            <Alert
              dismissible
              onClose={() => dispatch(alertActions.clear())}
              variant={alert.type === 'alert-danger' ? 'danger' : 'success'}
              style={{ textAlign: 'center', width: '100%' }}
            >
              {alert.message}
            </Alert>
          </Row>
        ) : null}
        {isLoading ? <Loading /> : table()}
      </>
    );
  };

  const footer = () => {
    return (
      <>
        <Row className="vCenter" style={{ margin: '0 auto' }}>
          <Button
            variant="outline-primary"
            className="formButton"
            onClick={add}
            disabled={isLoading}
          >
            Add team
          </Button>
        </Row>
        <Row />
      </>
    );
  };

  const headerMsg = () => {
    return <span>Team Admin</span>;
  };

  const listModal = () => {
    return (
      <>
        <Modal.Header className="searchHeader">
          {headerMsg()}
          {numTeams !== 0 && data?.length !== 0 ? (
            <SearchInput searchTerm={searchTerm} search={handleSearch} />
          ) : null}
        </Modal.Header>
        <Modal.Body className="setupForm" onScroll={handleScroll}>
          {adminBody()}
          {isScrollLoading ? <ScrollLoading /> : null}
        </Modal.Body>
        <Modal.Footer className="setupFooter">{footer()}</Modal.Footer>
      </>
    );
  };

  const modal = () => {
    switch (mode) {
      case ADD:
        return <TeamForm back={back} />;
      case EDIT:
        break;
      case DELETE:
        break;
      case LIST:
        return listModal();
      case DETAIL:
        return <TeamDetail back={back} teamId={teamId} />;
      default:
        return null;
    }
  };

  return modal();
}

export default TeamAdmin;
